import { useState, useEffect } from 'react';
import ChatTitle from '@/modules/chat/components/aiChat/components/title/ChatTitle';
import ChatView from '@/modules/chat/components/aiChat/components/chatView/ChatView';
import FloatingResizablePanel from '@/modules/chat/components/floatingPanel/FloatingResizablePanel';
import ConversationList from '@/modules/chat/components/aiChat/components/conversationList/ConversationList';
import { AiChatProps } from '../../types/types';
import { AuthorType } from '../../types/message';

const AiChat = (props: AiChatProps): React.ReactElement | null => {
  const {
    messages,
    loading,
    promptSuggestions,
    sendMessage,
    onArtifactClick,
    setChatVisible,
    chatVisible,
    artifacts,
    pendingToolApprovals,
    reviewApprovals,
    primaryColor,
    secondaryColor,
    aiMessageIcon,
    loadingLottieUrl,
    lastScrollTop,
    isFullscreen,
    setIsFullscreen,
    // Conversation management
    conversations,
    currentConversationId,
    createNewConversation,
    selectConversation,
    deleteConversation,
  } = props;

  const [showConversationList, setShowConversationList] = useState(!currentConversationId);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);

  // When a conversation is selected, show the chat view
  useEffect(() => {
    if (currentConversationId) {
      setShowConversationList(false);
      
      // Check if it's a new conversation (no messages yet)
      const currentConv = conversations.find(c => c.id === currentConversationId);
      if (currentConv && currentConv.messages.length === 0) {
        setShowPrivacyPopup(true);
      }
    } else {
      // If no conversation is selected and we are in the list, 
      // it might be the first time opening the chat
      if (conversations.length === 0) {
        setShowPrivacyPopup(true);
      }
    }
  }, [currentConversationId, conversations]);

  if (!chatVisible) {
    return null;
  }

  const handleBackClick = (): void => {
    console.log('Navigating back to conversation list');
    setShowConversationList(true);
  };

  const handleSelectConversation = (conversationId: string): void => {
    selectConversation(conversationId);
    setShowConversationList(false);
  };

  const handleCreateNewConversation = async (firstMessage?: string): Promise<void> => {
    if (firstMessage) {
      // Create conversation and send message in one operation
      await createNewConversation(firstMessage, {
        authorType: AuthorType.USER,
        content: firstMessage,
      });
    } else {
      await createNewConversation();
    }
    setShowConversationList(false);
  };

  const renderTitle = (): React.ReactNode => {
    if (showConversationList) {
      return null; // ConversationList has its own header
    }
    return <ChatTitle onBackClick={handleBackClick} />;
  };

  return (
    <FloatingResizablePanel
      onClose={() => setChatVisible(false)}
      title={renderTitle()}
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
      isFullscreen={isFullscreen}
      onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
    >
      {showConversationList ? (
        <ConversationList
          conversations={conversations}
          onSelectConversation={handleSelectConversation}
          onDeleteConversation={deleteConversation}
          onCreateNewConversation={handleCreateNewConversation}
          primaryColor={primaryColor}
          showPrivacyPopup={showPrivacyPopup}
          setShowPrivacyPopup={setShowPrivacyPopup}
        />
      ) : (
        <ChatView
          artifacts={artifacts}
          messages={messages ?? []}
          loading={loading}
          onSend={sendMessage}
          promptSuggestions={promptSuggestions}
          onArtifactClick={onArtifactClick}
          pendingToolApprovals={pendingToolApprovals}
          reviewApprovals={reviewApprovals}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          aiMessageIcon={aiMessageIcon}
          loadingLottieUrl={loadingLottieUrl}
          lastScrollTop={lastScrollTop}
          showPrivacyPopup={showPrivacyPopup}
          setShowPrivacyPopup={setShowPrivacyPopup}
        />
      )}
    </FloatingResizablePanel>
  );
};

export default AiChat;
